import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { todayBR } from "../lib/dates";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { Plus, Users, HandCoins, Trash2, Bell, Pencil } from "lucide-react";
import LoanDetailsDialog, { formatBRL, formatDate, dueState } from "./LoanDetailsDialog";
import LoansSummary, { addPeriods } from "./LoansSummary";
import LoansMonthly from "./LoansMonthly";
import LoansByCustomer from "./LoansByCustomer";
import LoanMonthDialog from "./LoanMonthDialog";
import CustomerLoansDialog from "./CustomerLoansDialog";

export default function LoansPanel() {
  const [customers, setCustomers] = useState([]);
  const [loanData, setLoanData] = useState({
    loans: [],
    total_principal: 0,
    total_receivable: 0,
    total_paid: 0,
    outstanding: 0,
    expected_profit: 0,
  });
  const [loading, setLoading] = useState(true);

  // Dialogs
  const [customerDlg, setCustomerDlg] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [loanDlg, setLoanDlg] = useState(false);
  const [editingLoan, setEditingLoan] = useState(null);
  const [paymentDlg, setPaymentDlg] = useState(null); // loan object
  const [confirmDelete, setConfirmDelete] = useState(null); // {type,id}

  // Customer form
  const [custName, setCustName] = useState("");
  const [custPhone, setCustPhone] = useState("");

  // Loan form
  const [loanCustomerId, setLoanCustomerId] = useState("");
  const [loanPrincipal, setLoanPrincipal] = useState("");
  const [loanRate, setLoanRate] = useState("");
  const [loanTotal, setLoanTotal] = useState("");
  const [loanFrequency, setLoanFrequency] = useState("monthly");
  const [loanInstallments, setLoanInstallments] = useState("1");
  const [loanStart, setLoanStart] = useState(todayBR());
  const [loanFirstPayment, setLoanFirstPayment] = useState("");
  const [loanNotes, setLoanNotes] = useState("");
  const [loanSource, setLoanSource] = useState("");
  const [filterSource, setFilterSource] = useState("all");

  const openNewCustomer = () => {
    setEditingCustomer(null);
    setCustName("");
    setCustPhone("");
    setCustomerDlg(true);
  };
  const openEditCustomer = (c) => {
    setEditingCustomer(c);
    setCustName(c.name || "");
    setCustPhone(c.phone || "");
    setCustomerDlg(true);
  };
  const openNewLoan = () => {
    setEditingLoan(null);
    setLoanCustomerId("");
    setLoanPrincipal("");
    setLoanRate("");
    setLoanTotal("");
    setLoanFrequency("monthly");
    setLoanInstallments("1");
    setLoanStart(todayBR());
    setLoanFirstPayment(addPeriods(todayBR(), "monthly", 1));
    setLoanNotes("");
    setLoanSource("");
    setLoanDlg(true);
  };
  const openEditLoan = (l) => {
    setEditingLoan(l);
    setLoanCustomerId(l.customer_id);
    setLoanPrincipal(String(l.principal));
    setLoanRate(String(l.interest_rate_percent));
    setLoanTotal(String(l.total_receivable));
    setLoanFrequency(l.frequency);
    setLoanInstallments(String(l.num_installments));
    setLoanStart(l.start_date);
    setLoanFirstPayment(l.first_payment_date || "");
    setLoanNotes(l.notes || "");
    setLoanSource(l.source || "");
    setDetailsLoan(null);
    setLoanDlg(true);
  };
  const calcTotal = (principal, rate, n) => {
    const P = parseFloat(principal), r = parseFloat(rate), k = parseInt(n, 10);
    if (!P || isNaN(r) || !k) return "";
    return (P * (1 + (r / 100) * k)).toFixed(2);
  };
  const calcRate = (principal, total, n) => {
    const P = parseFloat(principal), T = parseFloat(total), k = parseInt(n, 10);
    if (!P || !T || !k) return "";
    return Math.max(0, ((T / P - 1) / k) * 100).toFixed(2).replace(/\.?0+$/, "");
  };
  const changeRate = (v) => { setLoanRate(v); setLoanTotal(calcTotal(loanPrincipal, v, loanInstallments)); };
  const changeTotal = (v) => { setLoanTotal(v); setLoanRate(calcRate(loanPrincipal, v, loanInstallments)); };
  const changePrincipal = (v) => { setLoanPrincipal(v); if (loanRate !== "") setLoanTotal(calcTotal(v, loanRate, loanInstallments)); };
  const changeInstallments = (v) => { setLoanInstallments(v); if (loanRate !== "") setLoanTotal(calcTotal(loanPrincipal, loanRate, v)); };
  const changeFrequency = (f) => {
    setLoanFrequency(f);
    setLoanFirstPayment(f === "free" ? "" : addPeriods(loanStart, f, 1));
  };
  const changeStart = (v) => {
    setLoanStart(v);
    if (loanFrequency !== "free" && v) setLoanFirstPayment(addPeriods(v, loanFrequency, 1));
  };

  // Payment form
  const [payDate, setPayDate] = useState(todayBR());
  const [payAmount, setPayAmount] = useState("");
  const [payInterestOnly, setPayInterestOnly] = useState(false);

  // Filter
  const [filterCustomer, setFilterCustomer] = useState("all");
  const [filterStatus, setFilterStatus] = useState("open");
  const [periodType, setPeriodType] = useState("month"); // month|year|all
  const [periodMonth, setPeriodMonth] = useState(() => todayBR().slice(0, 7));
  const [periodYear, setPeriodYear] = useState(() => todayBR().slice(0, 4));
  const [detailsLoan, setDetailsLoan] = useState(null);
  const [monthKey, setMonthKey] = useState(null);
  const [historyCustomer, setHistoryCustomer] = useState(null);
  const today = todayBR();

  const load = useCallback(async () => {
    try {
      const [c, l] = await Promise.all([api.get("/customers"), api.get("/loans")]);
      setCustomers(c.data || []);
      setLoanData(l.data || { loans: [] });
      setDetailsLoan((cur) => (cur ? (l.data?.loans || []).find((x) => x.id === cur.id) || null : null));
    } catch {
      toast.error("Erro ao carregar empréstimos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const customerById = useMemo(() => {
    const m = new Map();
    customers.forEach((c) => m.set(c.id, c));
    return m;
  }, [customers]);

  const sources = useMemo(() => {
    const set = new Set();
    for (const l of loanData.loans) if (l.source) set.add(l.source);
    return [...set].sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [loanData.loans]);
  const sourceLoans = useMemo(
    () => (filterSource === "all" ? loanData.loans : loanData.loans.filter((l) => (l.source || "") === filterSource)),
    [loanData.loans, filterSource]
  );

  const dueLoans = useMemo(
    () => loanData.loans.filter((l) => dueState(l, today)),
    [loanData.loans, today]
  );

  const notifiedRef = React.useRef(false);
  useEffect(() => {
    if (loading || notifiedRef.current || dueLoans.length === 0) return;
    notifiedRef.current = true;
    toast(`${dueLoans.length} empréstimo${dueLoans.length > 1 ? "s" : ""} com recebimento hoje ou atrasado`, {
      icon: <Bell className="h-4 w-4 text-[#D4FF00]" />,
    });
  }, [loading, dueLoans]);

  const filteredLoans = useMemo(() => {
    let list = sourceLoans.filter((l) =>
      filterStatus === "open" ? l.status !== "paid" : l.status === "paid"
    );
    if (filterCustomer !== "all") list = list.filter((l) => l.customer_id === filterCustomer);
    const rank = (l) => (dueState(l, today) === "late" ? 0 : dueState(l, today) === "today" ? 1 : 2);
    return [...list].sort((a, b) => rank(a) - rank(b));
  }, [sourceLoans, filterCustomer, filterStatus, today]);

  const saveCustomer = async () => {
    if (!custName.trim()) {
      toast.error("Nome obrigatório");
      return;
    }
    try {
      const payload = { name: custName.trim(), phone: custPhone.trim() || null };
      if (editingCustomer) {
        await api.put(`/customers/${editingCustomer.id}`, payload);
        toast.success("Cliente atualizado");
      } else {
        await api.post("/customers", payload);
        toast.success("Cliente cadastrado");
      }
      setCustomerDlg(false);
      setEditingCustomer(null);
      setCustName("");
      setCustPhone("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    }
  };

  const saveLoan = async () => {
    if (!loanCustomerId) return toast.error("Selecione o cliente");
    const principal = parseFloat(loanPrincipal);
    const rate = parseFloat(loanRate);
    const n = parseInt(loanInstallments, 10);
    if (!principal || principal <= 0) return toast.error("Valor principal inválido");
    if (isNaN(rate) || rate < 0) return toast.error("Taxa inválida");
    if (!n || n < 1) return toast.error("Nº de parcelas ≥ 1");
    if (loanFrequency !== "free" && !loanFirstPayment) return toast.error("Informe a data do 1º pagamento");
    try {
      const payload = {
        customer_id: loanCustomerId,
        principal,
        interest_rate_percent: rate,
        frequency: loanFrequency,
        num_installments: n,
        start_date: loanStart,
        first_payment_date: loanFirstPayment || null,
        notes: loanNotes || null,
        source: loanSource.trim() || null,
      };
      if (editingLoan) {
        await api.put(`/loans/${editingLoan.id}`, payload);
        toast.success("Empréstimo atualizado");
      } else {
        await api.post("/loans", payload);
        toast.success("Empréstimo registrado");
      }
      setLoanDlg(false);
      setEditingLoan(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    }
  };

  const addPayment = async () => {
    const amt = parseFloat(payAmount);
    if (!amt || amt <= 0) return toast.error("Valor inválido");
    try {
      await api.post(`/loans/${paymentDlg.id}/payments`, { date: payDate, amount: amt, interest_only: payInterestOnly });
      toast.success(payInterestOnly ? "Juros recebidos — vencimento rolado para o próximo período" : "Pagamento registrado");
      setPaymentDlg(null);
      setPayAmount("");
      setPayInterestOnly(false);
      setPayDate(todayBR());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      const url = confirmDelete.type === "loan" ? `/loans/${confirmDelete.id}` : `/customers/${confirmDelete.id}`;
      await api.delete(url);
      toast.success("Removido");
      setConfirmDelete(null);
      setDetailsLoan(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao remover");
    }
  };

  if (loading) {
    return <div className="h-40 animate-pulse rounded-xl border border-white/10 bg-[#121212]" />;
  }

  return (
    <div className="space-y-6">
      {/* Period filter + summary cards */}
      <div className="rounded-xl border border-white/10 bg-[#121212] p-4 md:p-5">
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <div className="flex rounded-full border border-white/10 bg-black/40 p-0.5">
            {[["month", "Mensal"], ["year", "Anual"], ["all", "Todos"]].map(([v, label]) => (
              <button
                key={v}
                data-testid={`loans-period-${v}`}
                onClick={() => setPeriodType(v)}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold ${
                  periodType === v ? "bg-[#D4FF00] text-black" : "text-zinc-400 hover:text-white"
                }`}
                style={{ transition: "background-color 0.2s ease, color 0.2s ease" }}
              >
                {label}
              </button>
            ))}
          </div>
          {periodType === "month" && (
            <Input
              data-testid="loans-period-month"
              type="month"
              value={periodMonth}
              onChange={(e) => e.target.value && setPeriodMonth(e.target.value)}
              className="h-9 w-[10rem] border-white/10 bg-black/40 font-mono-num text-sm"
            />
          )}
          {periodType === "year" && (
            <Input
              data-testid="loans-period-year"
              type="number"
              min="2000"
              max="2100"
              value={periodYear}
              onChange={(e) => setPeriodYear(e.target.value)}
              className="h-9 w-[6rem] border-white/10 bg-black/40 font-mono-num text-sm"
            />
          )}
          {sources.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 sm:ml-2">
              <span className="text-[0.62rem] uppercase tracking-widest text-zinc-500">Caixa</span>
              {[["all", "Todos"], ...sources.map((x) => [x, x])].map(([v, label]) => (
                <button
                  key={v}
                  data-testid={`loans-source-${v}`}
                  onClick={() => setFilterSource(v)}
                  className={`rounded-full border px-2.5 py-1 text-[0.7rem] font-semibold ${
                    filterSource === v
                      ? "border-[#22C55E] bg-[#22C55E]/20 text-[#22C55E]"
                      : "border-white/10 text-zinc-400 hover:border-white/30 hover:text-white"
                  }`}
                  style={{ transition: "background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease" }}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
          <span data-testid="loans-period-label" className="ml-auto text-[0.7rem] uppercase tracking-widest text-zinc-500">
            {periodType === "all"
              ? "Todos os empréstimos"
              : periodType === "year"
              ? `Parcelas previstas em ${periodYear}`
              : periodMonth === todayBR().slice(0, 7)
              ? "Parcelas previstas · mês atual"
              : `Parcelas previstas · ${periodMonth.split("-").reverse().join("/")}`}
          </span>
        </div>
        <LoansSummary
          loans={sourceLoans}
          period={{ type: periodType, month: periodMonth, year: String(periodYear) }}
          today={today}
        />
      </div>

      <LoansMonthly loans={sourceLoans} today={today} onMonthClick={setMonthKey} />

      {/* Due notification banner */}
      {dueLoans.length > 0 && (
        <div
          data-testid="loans-due-banner"
          className="flex flex-col gap-2 rounded-xl border border-[#D4FF00]/30 bg-[#D4FF00]/10 p-4 sm:flex-row sm:items-center sm:justify-between"
        >
          <div className="flex items-start gap-3">
            <Bell className="mt-0.5 h-5 w-5 shrink-0 animate-pulse text-[#D4FF00]" />
            <div>
              <div className="text-sm font-bold text-white">
                {dueLoans.length} recebimento{dueLoans.length > 1 ? "s" : ""} para hoje ou em atraso
              </div>
              <div className="mt-0.5 text-xs text-zinc-300">
                {dueLoans.slice(0, 3).map((l) => (
                  <span key={l.id} className="mr-3">
                    {customerById.get(l.customer_id)?.name || "Cliente"} · {formatBRL(l.installment_value)} ·{" "}
                    <span className={dueState(l, today) === "late" ? "text-red-300" : "text-[#D4FF00]"}>
                      {dueState(l, today) === "late" ? `atrasado (${formatDate(l.next_due_date)})` : "hoje"}
                    </span>
                  </span>
                ))}
                {dueLoans.length > 3 && <span>+{dueLoans.length - 3}</span>}
              </div>
            </div>
          </div>
          <Button
            data-testid="loans-due-banner-view"
            onClick={() => { setFilterStatus("open"); setDetailsLoan(dueLoans[0]); }}
            className="h-8 shrink-0 rounded-full bg-[#D4FF00] px-4 text-xs font-bold text-black hover:bg-[#c4ee00]"
          >
            Ver
          </Button>
        </div>
      )}

      {/* Loans block */}
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="font-display text-2xl font-bold text-white">Empréstimos</h2>
          <p className="text-xs text-zinc-500">Semanal, mensal ou livre, com controle de parcelas.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-full border border-white/10 bg-[#121212] p-0.5">
            {[["open", "Abertos"], ["paid", "Concluídos"]].map(([v, label]) => (
              <button
                key={v}
                data-testid={`filter-loans-status-${v}`}
                onClick={() => setFilterStatus(v)}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold ${
                  filterStatus === v ? "bg-[#D4FF00] text-black" : "text-zinc-400 hover:text-white"
                }`}
                style={{ transition: "background-color 0.2s ease, color 0.2s ease" }}
              >
                {label}
              </button>
            ))}
          </div>
          <Select value={filterCustomer} onValueChange={setFilterCustomer}>
            <SelectTrigger
              data-testid="filter-loans-customer"
              className="h-9 w-[180px] border-white/10 bg-[#121212] text-xs"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border-white/10 bg-[#121212] text-zinc-200">
              <SelectItem value="all">Todos clientes</SelectItem>
              {customers.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            data-testid="new-loan-btn"
            onClick={openNewLoan}
            disabled={customers.length === 0}
            className="h-10 gap-2 rounded-full bg-[#D4FF00] px-5 font-bold text-black hover:bg-[#c4ee00] disabled:opacity-40"
          >
            <Plus className="h-4 w-4" />
            Novo empréstimo
          </Button>
        </div>
      </div>

      {filteredLoans.length === 0 ? (
        <div
          data-testid="loans-empty"
          className="flex flex-col items-center justify-center rounded-xl border border-dashed border-white/10 bg-[#121212] p-16 text-center"
        >
          <HandCoins className="mb-3 h-10 w-10 text-zinc-700" strokeWidth={1.5} />
          <h3 className="font-display text-lg font-bold text-white">
            {filterStatus === "paid" ? "Nenhum empréstimo concluído" : "Nenhum empréstimo em aberto"}
          </h3>
          <p className="mt-1 max-w-sm text-sm text-zinc-500">
            {customers.length === 0
              ? "Cadastre um cliente antes de criar um empréstimo."
              : filterStatus === "paid"
              ? "Empréstimos quitados aparecerão aqui."
              : "Registre um empréstimo para começar a acompanhar."}
          </p>
        </div>
      ) : (
        <LoansByCustomer
          loans={filteredLoans}
          customerById={customerById}
          today={today}
          onOpenLoan={setDetailsLoan}
          statusLabel={filterStatus === "paid" ? "concluído(s)" : "em aberto"}
        />
      )}

      <LoanDetailsDialog
        loan={detailsLoan}
        customer={detailsLoan ? customerById.get(detailsLoan.customer_id) : null}
        today={today}
        onClose={() => setDetailsLoan(null)}
        onAddPayment={(l) => {
          setPaymentDlg(l);
          setPayDate(todayBR());
          setPayAmount(String(l.installment_value));
          setPayInterestOnly(false);
        }}
        onDelete={(l) => setConfirmDelete({ type: "loan", id: l.id })}
        onEdit={openEditLoan}
      />

      <LoanMonthDialog
        monthKey={monthKey}
        loans={sourceLoans}
        customerById={customerById}
        today={today}
        onClose={() => setMonthKey(null)}
        onOpenLoan={(l) => { setMonthKey(null); setDetailsLoan(l); }}
      />

      <CustomerLoansDialog
        customer={historyCustomer}
        loans={loanData.loans}
        today={today}
        onClose={() => setHistoryCustomer(null)}
        onOpenLoan={(l) => { setHistoryCustomer(null); setDetailsLoan(l); }}
      />

      {/* Customers block */}
      <div className="rounded-xl border border-white/10 bg-[#121212] p-5">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-[#D4FF00]" />
              <h3 className="font-display text-lg font-bold text-white">Clientes</h3>
            </div>
            <p className="text-xs text-zinc-500">Cadastre pessoas para acompanhar empréstimos individuais.</p>
          </div>
          <Button
            data-testid="new-customer-btn"
            onClick={openNewCustomer}
            className="h-9 gap-2 rounded-full bg-[#D4FF00] px-4 text-xs font-bold text-black hover:bg-[#c4ee00]"
          >
            <Plus className="h-3.5 w-3.5" />
            Novo cliente
          </Button>
        </div>

        {customers.length === 0 ? (
          <div className="py-6 text-center text-xs text-zinc-500">Nenhum cliente cadastrado ainda.</div>
        ) : (
          <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
            {customers.map((c) => {
              const clientLoans = loanData.loans.filter((l) => l.customer_id === c.id);
              const outstanding = clientLoans.reduce((s, l) => s + (l.total_receivable - l.total_paid), 0);
              return (
                <div
                  key={c.id}
                  data-testid={`customer-card-${c.id}`}
                  className="flex items-center justify-between rounded-lg border border-white/5 bg-black/30 px-3 py-2.5"
                >
                  <button
                    data-testid={`customer-history-${c.id}`}
                    onClick={() => setHistoryCustomer(c)}
                    className="min-w-0 flex-1 text-left"
                    title="Ver histórico de empréstimos"
                  >
                    <div className="truncate text-sm font-semibold text-white hover:text-[#D4FF00]" style={{ transition: "color 0.15s ease" }}>{c.name}</div>
                    <div className="mt-0.5 text-[0.75rem] text-zinc-500">
                      {c.phone ? `${c.phone} · ` : ""}{clientLoans.length} empréstimo{clientLoans.length === 1 ? "" : "s"} · A receber {formatBRL(outstanding)}
                      <span className="ml-1 text-[#D4FF00]/80">· Histórico</span>
                    </div>
                  </button>
                  <Button
                    data-testid={`edit-customer-${c.id}`}
                    variant="ghost"
                    size="icon"
                    onClick={() => openEditCustomer(c)}
                    className="h-8 w-8 text-zinc-500 hover:bg-white/5 hover:text-white"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    data-testid={`delete-customer-${c.id}`}
                    variant="ghost"
                    size="icon"
                    onClick={() => setConfirmDelete({ type: "customer", id: c.id })}
                    className="h-8 w-8 text-zinc-500 hover:bg-red-500/10 hover:text-red-400"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Customer dialog */}
      <Dialog open={customerDlg} onOpenChange={setCustomerDlg}>
        <DialogContent data-testid="customer-form-dialog" className="border-white/10 bg-[#121212]">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold text-white">{editingCustomer ? "Editar cliente" : "Novo cliente"}</DialogTitle>
            <DialogDescription className="text-sm text-zinc-500">
              Cadastre alguém para acompanhar empréstimos individualmente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-1.5 block text-xs uppercase tracking-widest text-zinc-500">Nome</Label>
              <Input
                data-testid="customer-name-input"
                value={custName}
                onChange={(e) => setCustName(e.target.value)}
                placeholder="Ex: João Silva"
                className="bg-black/40"
              />
            </div>
            <div>
              <Label className="mb-1.5 block text-xs uppercase tracking-widest text-zinc-500">Telefone (opcional)</Label>
              <Input
                data-testid="customer-phone-input"
                value={custPhone}
                onChange={(e) => setCustPhone(e.target.value)}
                placeholder="(11) 99999-9999"
                className="bg-black/40 font-mono-num"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCustomerDlg(false)} className="text-zinc-400 hover:text-white">
              Cancelar
            </Button>
            <Button
              data-testid="save-customer-btn"
              onClick={saveCustomer}
              className="bg-[#D4FF00] font-bold text-black hover:bg-[#c4ee00]"
            >
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Loan dialog */}
      <Dialog open={loanDlg} onOpenChange={setLoanDlg}>
        <DialogContent data-testid="loan-form-dialog" className="border-white/10 bg-[#121212]">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold text-white">{editingLoan ? "Editar empréstimo" : "Novo empréstimo"}</DialogTitle>
            <DialogDescription className="text-sm text-zinc-500">
              Escolha a frequência de recebimento e a rentabilidade.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-1.5 block text-xs uppercase tracking-widest text-zinc-500">Cliente</Label>
              <Select value={loanCustomerId} onValueChange={setLoanCustomerId}>
                <SelectTrigger data-testid="loan-customer-select" className="bg-black/40">
                  <SelectValue placeholder="Selecione o cliente" />
                </SelectTrigger>
                <SelectContent className="border-white/10 bg-[#121212] text-zinc-200">
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <div>
                <Label className="mb-1.5 block text-xs uppercase tracking-widest text-zinc-500">Valor emprestado</Label>
                <Input
                  data-testid="loan-principal-input"
                  type="number"
                  step="0.01"
                  value={loanPrincipal}
                  onChange={(e) => changePrincipal(e.target.value)}
                  placeholder="1000.00"
                  className="bg-black/40 font-mono-num"
                />
              </div>
              <div>
                <Label className="mb-1.5 block text-xs uppercase tracking-widest text-zinc-500">
                  Taxa por parcela (%)
                </Label>
                <Input
                  data-testid="loan-rate-input"
                  type="number"
                  step="0.01"
                  value={loanRate}
                  onChange={(e) => changeRate(e.target.value)}
                  placeholder="10"
                  className="bg-black/40 font-mono-num"
                />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <Label className="mb-1.5 block text-xs uppercase tracking-widest text-zinc-500">
                  Total a receber (R$)
                </Label>
                <Input
                  data-testid="loan-total-input"
                  type="number"
                  step="0.01"
                  value={loanTotal}
                  onChange={(e) => changeTotal(e.target.value)}
                  placeholder="Ex: 1200"
                  className="bg-black/40 font-mono-num text-[#D4FF00]"
                />
              </div>
            </div>
            <p className="-mt-2 text-[0.7rem] text-zinc-500">Preencha a taxa <span className="text-zinc-300">ou</span> o total a receber — o outro é calculado automaticamente.</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="mb-1.5 block text-xs uppercase tracking-widest text-zinc-500">Tipo</Label>
                <Select value={loanFrequency} onValueChange={changeFrequency}>
                  <SelectTrigger data-testid="loan-frequency-select" className="bg-black/40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-[#121212] text-zinc-200">
                    <SelectItem value="monthly">Mensal</SelectItem>
                    <SelectItem value="weekly">Semanal</SelectItem>
                    <SelectItem value="free">Livre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1.5 block text-xs uppercase tracking-widest text-zinc-500">Nº de parcelas</Label>
                <Input
                  data-testid="loan-installments-input"
                  type="number"
                  min="1"
                  value={loanInstallments}
                  onChange={(e) => changeInstallments(e.target.value)}
                  className="bg-black/40 font-mono-num"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="mb-1.5 block text-xs uppercase tracking-widest text-zinc-500">Data de início</Label>
                <Input
                  data-testid="loan-start-input"
                  type="date"
                  value={loanStart}
                  onChange={(e) => changeStart(e.target.value)}
                  className="bg-black/40 font-mono-num"
                />
              </div>
              <div>
                <Label className="mb-1.5 block text-xs uppercase tracking-widest text-zinc-500">
                  1º pagamento{loanFrequency === "free" ? " (opcional)" : ""}
                </Label>
                <Input
                  data-testid="loan-first-payment-input"
                  type="date"
                  value={loanFirstPayment}
                  onChange={(e) => setLoanFirstPayment(e.target.value)}
                  className="bg-black/40 font-mono-num"
                />
              </div>
            </div>
            <p className="-mt-2 text-[0.7rem] text-zinc-500">
              {loanFrequency === "free"
                ? "Livre: sem calendário fixo. Se informar a data, você recebe um lembrete nesse dia."
                : `As demais parcelas seguem a cada ${loanFrequency === "weekly" ? "semana" : "mês"} a partir do 1º pagamento.`}
            </p>
            <div>
              <Label className="mb-1.5 block text-xs uppercase tracking-widest text-zinc-500">
                Origem do dinheiro (caixa)
              </Label>
              <Input
                data-testid="loan-source-input"
                list="loan-source-options"
                value={loanSource}
                onChange={(e) => setLoanSource(e.target.value)}
                placeholder="Ex: Caixa pessoal, Banca Bet365, Sócio..."
                className="bg-black/40"
              />
              <datalist id="loan-source-options">
                {sources.map((x) => <option key={x} value={x} />)}
              </datalist>
              {sources.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {sources.map((x) => (
                    <button
                      key={x}
                      type="button"
                      onClick={() => setLoanSource(x)}
                      className={`rounded-full border px-2 py-0.5 text-[0.7rem] ${
                        loanSource === x ? "border-[#22C55E] text-[#22C55E]" : "border-white/10 text-zinc-400 hover:text-white"
                      }`}
                    >
                      {x}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {/* Preview */}
            {loanPrincipal && loanRate && loanInstallments && (
              <div className="rounded-lg border border-white/10 bg-black/30 p-3 text-xs">
                <div className="mb-1 text-[0.7rem] uppercase tracking-widest text-zinc-500">Prévia</div>
                {(() => {
                  const p = parseFloat(loanPrincipal) || 0;
                  const r = parseFloat(loanRate) || 0;
                  const n = parseInt(loanInstallments, 10) || 1;
                  const total = p * (1 + (r / 100) * n);
                  const inst = total / n;
                  return (
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <div className="text-[0.7rem] text-zinc-500">Parcela</div>
                        <div className="font-mono-num text-sm font-bold text-white">{formatBRL(inst)}</div>
                      </div>
                      <div>
                        <div className="text-[0.7rem] text-zinc-500">Total</div>
                        <div className="font-mono-num text-sm font-bold text-[#D4FF00]">{formatBRL(total)}</div>
                      </div>
                      <div>
                        <div className="text-[0.7rem] text-zinc-500">Juros</div>
                        <div className="font-mono-num text-sm font-bold text-[#A78BFA]">{formatBRL(total - p)}</div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setLoanDlg(false)} className="text-zinc-400 hover:text-white">
              Cancelar
            </Button>
            <Button
              data-testid="save-loan-btn"
              onClick={saveLoan}
              className="bg-[#D4FF00] font-bold text-black hover:bg-[#c4ee00]"
            >
              {editingLoan ? "Atualizar" : "Registrar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment dialog */}
      <Dialog open={!!paymentDlg} onOpenChange={(o) => !o && setPaymentDlg(null)}>
        <DialogContent data-testid="payment-form-dialog" className="border-white/10 bg-[#121212]">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold text-white">Registrar recebimento</DialogTitle>
            <DialogDescription className="text-sm text-zinc-500">
              {paymentDlg && customerById.get(paymentDlg.customer_id)?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="mb-1.5 block text-xs uppercase tracking-widest text-zinc-500">Data</Label>
                <Input
                  data-testid="payment-date-input"
                  type="date"
                  value={payDate}
                  onChange={(e) => setPayDate(e.target.value)}
                  className="bg-black/40 font-mono-num"
                />
              </div>
              <div>
                <Label className="mb-1.5 block text-xs uppercase tracking-widest text-zinc-500">Valor (R$)</Label>
                <Input
                  data-testid="payment-amount-input"
                  type="number"
                  step="0.01"
                  value={payAmount}
                  onChange={(e) => setPayAmount(e.target.value)}
                  className="bg-black/40 font-mono-num"
                />
              </div>
            </div>
            {paymentDlg && (() => {
              const interest = Math.round(paymentDlg.principal * (paymentDlg.interest_rate_percent / 100) * 100) / 100;
              const remaining = Math.max(0, paymentDlg.total_receivable - paymentDlg.total_paid);
              return (
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    data-testid="payment-mode-installment"
                    onClick={() => { setPayInterestOnly(false); setPayAmount(String(paymentDlg.installment_value)); }}
                    className={`rounded-lg border p-3 text-left ${!payInterestOnly ? "border-[#D4FF00] bg-[#D4FF00]/10" : "border-white/10 bg-black/30 hover:border-white/25"}`}
                    style={{ transition: "border-color 0.2s ease, background-color 0.2s ease" }}
                  >
                    <div className={`text-[0.7rem] font-bold uppercase tracking-widest ${!payInterestOnly ? "text-[#D4FF00]" : "text-zinc-400"}`}>Parcela</div>
                    <div className="mt-1 font-mono-num text-base font-bold text-white">{formatBRL(paymentDlg.installment_value)}</div>
                    <div className="text-[0.62rem] text-zinc-500">Abate do total · restante {formatBRL(remaining)}</div>
                  </button>
                  <button
                    type="button"
                    data-testid="payment-mode-interest-only"
                    onClick={() => { setPayInterestOnly(true); setPayAmount(String(interest)); }}
                    className={`rounded-lg border p-3 text-left ${payInterestOnly ? "border-[#A78BFA] bg-[#A78BFA]/10" : "border-white/10 bg-black/30 hover:border-white/25"}`}
                    style={{ transition: "border-color 0.2s ease, background-color 0.2s ease" }}
                  >
                    <div className={`text-[0.7rem] font-bold uppercase tracking-widest ${payInterestOnly ? "text-[#A78BFA]" : "text-zinc-400"}`}>Só juros</div>
                    <div className="mt-1 font-mono-num text-base font-bold text-white">{formatBRL(interest)}</div>
                    <div className="text-[0.62rem] text-zinc-500">
                      Rola para o próximo {paymentDlg.frequency === "weekly" ? "semana" : "mês"} · total continua {formatBRL(remaining)}
                    </div>
                  </button>
                </div>
              );
            })()}
            {payInterestOnly && (
              <p data-testid="payment-interest-only-hint" className="rounded-md border border-[#A78BFA]/30 bg-[#A78BFA]/10 px-3 py-2 text-xs text-[#D8CCFF]">
                Você recebe só os juros agora. O valor total do empréstimo permanece devido e o vencimento passa para o próximo período. Pode repetir quantas vezes precisar.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPaymentDlg(null)} className="text-zinc-400 hover:text-white">
              Cancelar
            </Button>
            <Button
              data-testid="save-payment-btn"
              onClick={addPayment}
              className="bg-[#D4FF00] font-bold text-black hover:bg-[#c4ee00]"
            >
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="border-white/10 bg-[#121212]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Confirmar exclusão?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/10 bg-transparent text-zinc-300 hover:bg-white/5 hover:text-white">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-loan-btn"
              onClick={doDelete}
              className="bg-red-500 text-white hover:bg-red-600"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
